
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/providers/AuthProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "@/components/ui/use-toast";
import { useTranslation } from "react-i18next";

const Auth = () => {
	const { signIn, loading, user } = useAuth();
	const navigate = useNavigate();
	const [email, setEmail] = useState("");
	const [password, setPassword] = useState("");
	const [isLoading, setIsLoading] = useState(false);
	const [redirecting, setRedirecting] = useState(false);
	const { t } = useTranslation();

	// Redirect to dashboard if already logged in
	useEffect(() => {
		if (user && !loading && !redirecting) {
			setRedirecting(true);
			navigate("/");
		}
	}, [user, loading, navigate, redirecting]);

	// If redirecting, don't render anything to prevent flashes
	if (redirecting) {
		return null;
	}

	const handleSignIn = async (e: React.FormEvent) => {
		e.preventDefault();
		setIsLoading(true);

		try {
			// Use password sign in instead of provider
			const result = await signIn(email, password);
			
			if (!result.error) {
				toast({
					title: t('common.success'),
					description: t('auth.signInSuccess'),
				});
			}
		} catch (error) {
			toast({
				title: t('common.error'),
				description: error instanceof Error ? error.message : t('auth.signInError'),
				variant: "destructive",
			});
		} finally {
			setIsLoading(false);
		}
	};

	return (
		<div className="flex min-h-screen items-center justify-center bg-digger-light-gray p-4">
			<Card className="w-full max-w-md">
				<CardHeader className="text-center">
					<div className="mx-auto mb-4 flex items-center justify-center">
						<div className="bg-digger-blue-light rounded-md w-12 h-12 flex items-center justify-center mr-3">
							<img 
								src="/lovable-uploads/1f16b574-2585-4f89-a895-9af7f8c1fa72.png" 
								alt="digger logo icon" 
								className="w-7 h-7"
							/>
						</div>
						<span className="text-digger-blue text-2xl font-bold">Digger</span>
					</div>
					<CardTitle className="text-2xl">{t('auth.welcome')}</CardTitle>
					<CardDescription>
						{t('auth.platformDescription')}
					</CardDescription>
				</CardHeader>
				
				<form onSubmit={handleSignIn}>
					<CardContent className="space-y-4 pt-6">
						<div className="space-y-2">
							<label htmlFor="email" className="text-sm font-medium">
								{t('common.email')}
							</label>
							<Input
								id="email"
								type="email"
								value={email}
								onChange={(e) => setEmail(e.target.value)}
								placeholder={t('auth.emailPlaceholder')}
								required
							/>
						</div>
						<div className="space-y-2">
							<label htmlFor="password" className="text-sm font-medium">
								{t('common.password')}
							</label>
							<Input
								id="password"
								type="password"
								value={password}
								onChange={(e) => setPassword(e.target.value)}
								placeholder="••••••••"
								required
							/>
						</div>
					</CardContent>
					<CardFooter>
						<Button 
							type="submit" 
							className="w-full bg-digger-blue hover:bg-digger-blue-light"
							disabled={isLoading}
						>
							{isLoading ? t('auth.signingIn') : t('common.login')}
						</Button>
					</CardFooter>
				</form>
			</Card>
		</div>
	);
};

export default Auth;
